import { ENDPOINTS } from 'config/endpoints';
import { useMutation } from '@tanstack/react-query';
import client from 'utils/clients/client';
import { Request } from 'crono-fe-common/types/request';
import { Response } from 'crono-fe-common/types/response';
import CronoError from 'crono-fe-common/types/error';
import IntegrationType from 'crono-fe-common/types/enums/integrationType';
import { getLabelForIntegrationType } from 'crono-fe-common/utils/crm-utils';

export type AttioOauthCode = {
  code: string;
};

const integrationLabel = getLabelForIntegrationType(IntegrationType.ATTIO);

const ATTIO_CONNECT_ERROR = 'Error while connecting with the CRM';

// The check belongs here rather than in `onSuccess`: the success toast is
// emitted by the global mutation cache, which runs before the mutation's own
// callback, so failing any later would announce a connection that never
// happened. Only an explicit `false` counts — a response that omits the flag
// is reporting success.
const attioAuthCallback = async (data: AttioOauthCode) => {
  const request: Request = {
    url: ENDPOINTS.attio.authCallback,
    config: {
      method: 'post',
      data: data,
    },
  };

  const response: Response<null> = await client(request);

  if (response.data.isSuccess === false) {
    const error: CronoError = {
      message: ATTIO_CONNECT_ERROR,
      response: {
        status: response.status,
        data: { errors: response.data.errors ?? [] },
      },
    };
    throw error;
  }

  return response;
};

const useAttioAuthCallback = () =>
  useMutation<Response<null>, CronoError, AttioOauthCode>(attioAuthCallback, {
    meta: {
      successMessage: `Successfully logged into ${integrationLabel}`,
      errorMessage: ATTIO_CONNECT_ERROR,
    },
  });

export default useAttioAuthCallback;
